/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { getTradeHistory, getJournalsForTrade } from '../api/api';
import API_BASE_URL from '../api/config';
import JournalModal from './JournalModal';
import { formatStoredDateTime } from '../utils/datetime';

const SpecificTradeDetails = ({ tradeUuid, initialTradeData }) => {
  const [tradeData, setTradeData] = useState(initialTradeData);
  const [isLoading, setIsLoading] = useState(!initialTradeData);
  const [error, setError] = useState(null);
  
  const [journalsList, setJournalsList] = useState([]);
  const [journalsLoading, setJournalsLoading] = useState(false);
  const [journalsError, setJournalsError] = useState(null);

  const [isOrderHistoryVisible, setIsOrderHistoryVisible] = useState(true);
  const [isJournalsVisible, setIsJournalsVisible] = useState(true);
  const [showJournalModal, setShowJournalModal] = useState(false);

  // Lightbox state
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxImageSrc, setLightboxImageSrc] = useState('');

  // Effect for fetching main trade data
  useEffect(() => {
    const currentTradeId = tradeUuid || (initialTradeData ? initialTradeData.id : null);

    if (!currentTradeId) {
      setIsLoading(false);
      setError("No Trade ID available to fetch details.");
      setTradeData(null);
      setJournalsList([]); // Also clear journals
      return;
    }

    // If initialTradeData is provided and its ID matches currentTradeId, use it.
    if (initialTradeData && initialTradeData.id === currentTradeId) {
      setTradeData(initialTradeData);
      setIsLoading(false); // Main data is ready
    } else { 
      // Otherwise, fetch using currentTradeId (which would be from tradeUuid prop if initialData didn't match or wasn't there)
      setIsLoading(true);
      setError(null);
      getTradeHistory(currentTradeId)
        .then(data => {
          setTradeData(data);
        })
        .catch(err => {
          console.error("Error fetching trade history for ID:", currentTradeId, err);
          setError(err.message || `Failed to fetch trade details for ID: ${currentTradeId}.`);
          setTradeData(null);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [tradeUuid, initialTradeData]);

  // Fetch journals helper
  const fetchJournals = useCallback(() => {
    const idForJournalFetch = tradeData?.id || tradeUuid;
    if (idForJournalFetch) {
      setJournalsLoading(true);
      setJournalsError(null);
      getJournalsForTrade(idForJournalFetch)
        .then(fetchedJournals => {
          // Handle paginated response ({count, results, ...}) or plain array
          const journalsArray = Array.isArray(fetchedJournals)
            ? fetchedJournals
            : fetchedJournals?.results || [];
          setJournalsList(journalsArray);
        })
        .catch(err => {
          console.error("Error fetching journals for trade ID:", idForJournalFetch, err);
          setJournalsError(err.message || `Failed to fetch journals for trade ID: ${idForJournalFetch}.`);
          setJournalsList([]);
        })
        .finally(() => {
          setJournalsLoading(false);
        });
    } else {
      setJournalsList([]);
      setJournalsLoading(false);
      setJournalsError(null);
    }
  }, [tradeData, tradeUuid]);

  // Effect for fetching journals, dependent on a valid trade ID being determined and tradeData potentially set
  useEffect(() => {
    fetchJournals();
  }, [fetchJournals]);

  if (isLoading) {
    return <div className="p-4 text-center">Loading trade details...</div>;
  }

  if (error && !tradeData) { // Only show main error if tradeData itself failed to load
    return <div className="p-4 text-center text-red-500">Error loading trade: {error}</div>;
  }

  if (!tradeData) {
    return <div className="p-4 text-center">No trade data available.</div>;
  }

  // Destructure from tradeData, journals will come from journalsList state
  const {
    instrument,
    direction,
    lot_size,
    entry_price,
    stop_loss,
    actual_profit_loss,
    rr_ratio,
    trade_status,
    closed_at,
    created_at,
    order_history = []
  } = tradeData;

  const formatDate = (dateString) => {
    return formatStoredDateTime(dateString, undefined, 'N/A');
  };

  const getProfitColorClass = (profitValue) => {
    const numericProfit = parseFloat(profitValue);
    if (isNaN(numericProfit)) return 'text-textSecondary'; // Default for N/A or non-numeric (using textSecondary for neutral)
    if (numericProfit > 0) return 'text-success'; // Use theme's success color
    if (numericProfit < 0) return 'text-destructive'; // Use theme's destructive color
    return 'text-textSecondary'; // Neutral for zero
  };

  // Filter out orders that don't have a filled_at date
  const validOrderHistory = order_history.filter(order => order.filled_at);

  const openLightbox = (imageUrl) => {
    setLightboxImageSrc(imageUrl);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setLightboxImageSrc('');
  };

  return (
    <> {/* Fragment to wrap content and lightbox */}
    <div className="bg-backgroundSurface text-textPrimary p-3 md:p-4 rounded-lg shadow-md my-2 border border-borderDefault"> {/* Reduced padding slightly */}
      <h3 className="text-xl font-semibold mb-3 text-primary">Trade Details: {instrument}</h3>
      {/* Main trade details grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 mb-3 text-sm"> {/* Reduced gap-y and mb */}
        <p><strong className="font-medium text-textSecondary">Instrument:</strong> <span className="text-textPrimary">{instrument}</span></p>
        <p><strong className="font-medium text-textSecondary">Direction:</strong> <span className={direction === 'BUY' ? 'text-success' : 'text-destructive'}>{direction}</span></p>
        <p><strong className="font-medium text-textSecondary">Lot Size:</strong> <span className="text-textPrimary">{lot_size}</span></p>
        <p><strong className="font-medium text-textSecondary">Entry Price:</strong> <span className="text-textPrimary">{entry_price}</span></p>
        <p><strong className="font-medium text-textSecondary">Stop Loss:</strong> <span className="text-textPrimary">{stop_loss || 'N/A'}</span></p>
        <p><strong className="font-medium text-textSecondary">Status:</strong> <span className={`font-semibold ${trade_status === 'closed' ? 'text-textSecondary' : 'text-yellow-400 dark:text-yellow-300'}`}>{trade_status}</span></p>
        <p><strong className="font-medium text-textSecondary">P/L:</strong> <span className={getProfitColorClass(actual_profit_loss)}>{actual_profit_loss !== null ? parseFloat(actual_profit_loss).toFixed(2) : 'N/A'}</span></p>
        <p><strong className="font-medium text-textSecondary">RR Ratio:</strong> <span className="text-textPrimary">{rr_ratio || 'N/A'}</span></p>
        <p><strong className="font-medium text-textSecondary">Created At:</strong> <span className="text-textPrimary">{formatDate(created_at)}</span></p>
        <p><strong className="font-medium text-textSecondary">Closed At:</strong> <span className="text-textPrimary">{formatDate(closed_at)}</span></p>
      </div>

      {/* Order History Section */}
      <div className="mt-3"> {/* Reduced mt */}
        <button
          onClick={() => setIsOrderHistoryVisible(!isOrderHistoryVisible)}
          className="text-primary hover:text-primaryHover font-semibold mb-1 focus:outline-none" /* Reduced mb */
        >
          {isOrderHistoryVisible ? 'Hide' : 'Show'} Order History ({validOrderHistory.length})
        </button>
        {isOrderHistoryVisible && (
          <div className="overflow-x-auto">
            {validOrderHistory.length > 0 ? (
              <table className="min-w-full text-xs bg-backgroundSurface rounded border border-borderDefault">
                <thead className="bg-slate-50 dark:bg-slate-700"> {/* Generic header bg */}
                  <tr>
                    <th className="p-2 text-left">Filled At</th>
                    <th className="p-2 text-left">Type</th>
                    <th className="p-2 text-left">Direction</th>
                    <th className="p-2 text-right">Price</th>
                    <th className="p-2 text-right">Volume</th>
                    <th className="p-2 text-right">Profit</th>
                    <th className="p-2 text-right">Commission</th>
                    <th className="p-2 text-left">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {validOrderHistory.map((order) => (
                    <tr key={order.id} className="border-b border-borderDefault hover:bg-slate-50 dark:hover:bg-slate-700">
                      <td className="p-2">{formatDate(order.filled_at)}</td>
                      <td className="p-2">{order.order_type}</td>
                      <td className="p-2"><span className={order.direction === 'BUY' ? 'text-success' : 'text-destructive'}>{order.direction}</span></td>
                      <td className="p-2 text-right">{order.filled_price}</td>
                      <td className="p-2 text-right">{order.filled_volume}</td>
                      <td className="p-2 text-right"><span className={getProfitColorClass(order.profit)}>{order.profit !== null ? parseFloat(order.profit).toFixed(2) : 'N/A'}</span></td>
                      <td className="p-2 text-right">{order.commission !== null ? parseFloat(order.commission).toFixed(2) : 'N/A'}</td>
                      <td className="p-2">{order.deal_reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-gray-400">No order history available for this trade.</p>
            )}
          </div>
        )}
      </div>

      {/* Trade Journals Section */}
      <div className="mt-4">
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={() => setIsJournalsVisible(!isJournalsVisible)}
            className="text-primary hover:text-primaryHover font-semibold focus:outline-none text-lg"
          >
            {isJournalsVisible ? 'Hide' : 'Show'} Trade Journals ({journalsLoading ? '...' : journalsList.length})
          </button>
          <button
            onClick={() => setShowJournalModal(true)}
            className="px-3 py-1 text-sm bg-primary hover:bg-primaryHover text-white rounded-md transition-colors"
          >
            + Add Journal Entry
          </button>
        </div>
        {isJournalsVisible && (
          <div className="space-y-4">
            {journalsLoading && <p className="text-textSecondary">Loading journal entries...</p>}
            {journalsError && <p className="text-red-500">Error loading journals: {journalsError}</p>}
            {!journalsLoading && !journalsError && journalsList.length > 0 ? (
              journalsList.map((journal) => (
                <div key={journal.id} className="bg-backgroundAlt p-3 rounded-lg shadow border border-borderDefault"> {/* Reduced p */}
                  <h4 className="text-md font-semibold text-textPrimary mb-1">Journal Entry - {formatDate(journal.created_at)}</h4> {/* Reduced mb */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-sm mb-2"> {/* Reduced mb */}
                    <p><strong className="font-medium text-textSecondary">Action:</strong> <span className="text-textPrimary">{journal.action || 'N/A'}</span></p>
                    <p><strong className="font-medium text-textSecondary">Timeframe:</strong> <span className="text-textPrimary">{journal.trade_timeframe || 'N/A'}</span></p>
                    <p><strong className="font-medium text-textSecondary">Strategy Tag:</strong> <span className="text-textPrimary">{journal.strategy_tag || 'N/A'}</span></p>
                    <p><strong className="font-medium text-textSecondary">Emotional State:</strong> <span className="text-textPrimary">{journal.emotional_state || 'N/A'}</span></p>
                    <p className="md:col-span-2"><strong className="font-medium text-textSecondary">Market Condition:</strong> <span className="text-textPrimary">{journal.market_condition || 'N/A'}</span></p>
                    <p className="md:col-span-2"><strong className="font-medium text-textSecondary">Reason:</strong> <span className="text-textPrimary">{journal.reason || 'N/A'}</span></p>
                    {journal.details && <p className="md:col-span-2"><strong className="font-medium text-textSecondary">Details:</strong> <span className="text-textPrimary">{journal.details}</span></p>}
                  </div>
                  
                  {journal.attachments && journal.attachments.length > 0 && (
                    <div>
                      <h5 className="text-sm font-semibold text-textSecondary mt-2 mb-1">Attachments:</h5> {/* Reduced mt, mb */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2"> {/* Reduced gap */}
                        {journal.attachments.map(attachment => {
                          // Robust URL construction:
                          // If attachment.file is already an absolute URL, new URL() will use it.
                          // If attachment.file is relative (e.g., /media/...), it's joined with API_BASE_URL.
                          let imageUrl = null;
                          let displayFilename = 'Attachment';
                          if (attachment.file) {
                            try {
                                // Check if attachment.file is already a full URL
                                new URL(attachment.file); // if this doesn't throw, it's a full URL
                                imageUrl = attachment.file;
                            } catch {
                                // If not a full URL, assume it's relative and join with API_BASE_URL
                                imageUrl = new URL(attachment.file, API_BASE_URL).href;
                            }
                            displayFilename = imageUrl.substring(imageUrl.lastIndexOf('/') + 1);
                          }

                          return (
                            <div key={attachment.id} className="border border-borderDefault rounded p-2 text-center">
                              {imageUrl ? (
                                <div onClick={() => openLightbox(imageUrl)} className="cursor-pointer">
                                  <img 
                                    src={imageUrl} 
                                    alt="Journal Attachment Thumbnail" 
                                    className="w-full h-32 rounded object-contain mb-1 hover:opacity-80 transition-opacity"
                                    // onError will be handled by the browser's default broken image icon if src is invalid
                                  />
                                  <p className="text-xs text-textSecondary mt-1 truncate" title={displayFilename}>
                                    {displayFilename}
                                  </p>
                                  {/* Add a small text to show the attempted URL for debugging if image is broken,
                                      but this is hard to do reliably without knowing if it loaded.
                                      The browser's network inspector is the best tool for the user here.
                                      The previous onError modified the DOM directly which is not ideal React practice.
                                      Let's rely on alt text and browser tools.
                                      If API_BASE_URL is not set, images starting with /media/ will be relative to current host.
                                  */}
                                </div>
                              ) : (
                                <p className="text-xs text-textSecondary">Attachment (No preview or invalid path)</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                   {(!journal.attachments || journal.attachments.length === 0) && (
                     <p className="text-xs text-textSecondary mt-2">No attachments for this journal entry.</p>
                   )}
                </div>
              ))
            ) : (
              !journalsLoading && !journalsError && <p className="text-textSecondary">No journal entries found for this trade.</p>
            )}
          </div>
        )}
      </div>
    </div> {/* End of main content div */}

    {/* Journal Modal */}
    {showJournalModal && (
      <JournalModal
        tradeId={tradeData?.id || tradeUuid}
        initialSnapshotSymbol={instrument}
        onClose={() => {
          setShowJournalModal(false);
          fetchJournals(); // Refresh journals after adding
        }}
      />
    )}

    {/* Lightbox Modal */}
    {isLightboxOpen && (
      <div 
        className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-[100] p-4" // Higher z-index
        onClick={closeLightbox} // Close on overlay click
      >
        <div 
          className="relative bg-white p-2 rounded-lg shadow-xl max-w-[90vw] max-h-[90vh]"
          onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the image/modal content
        >
          <img 
            src={lightboxImageSrc} 
            alt="Enlarged journal attachment" 
            className="object-contain max-w-full max-h-[calc(90vh-40px)]" // Adjust max-h to account for padding/buttons
          />
          <button 
            onClick={closeLightbox}
            className="absolute top-2 right-2 text-black bg-white bg-opacity-50 hover:bg-opacity-75 rounded-full p-1 text-2xl leading-none"
            aria-label="Close lightbox"
          >
            &times;
          </button>
        </div>
      </div>
    )}
    </> // End of fragment
  );
};

SpecificTradeDetails.propTypes = {
  tradeUuid: PropTypes.string,
  initialTradeData: PropTypes.object,
};

export default SpecificTradeDetails;
